import { useEffect, useRef } from "react";
import { useReducedMotion, type Variants } from "framer-motion";

export const springEntrance: Variants = {
  hidden: { opacity: 0, y: 40, scale: 0.92 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { type: "spring", stiffness: 140, damping: 14, mass: 0.8 },
  },
};

export const headingFade: Variants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.28, ease: "easeOut" },
  },
};

export const stagger = (childDelay = 0.09): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: childDelay } },
});

/**
 * Returns a ref. Attach it to a card element to get a 3D mouse-tilt
 * effect bounded to ±maxTilt degrees, plus CSS custom properties
 * --mx and --my (0..100%) following the cursor for shine overlays.
 *
 * No-op on reduced motion or when the element doesn't exist.
 */
export function useCardTilt(maxTilt = 3) {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (reduced) return;
    const el = ref.current;
    if (!el) return;

    // Cache the bounding rect on mouseenter rather than reading it on every
    // mousemove. getBoundingClientRect is a layout-property read — calling
    // it inside a high-frequency event handler forces synchronous layout
    // reflows on the main thread, which Lighthouse flags as a perf issue.
    // The card's position relative to the viewport doesn't change while the
    // cursor is over it (no scroll while hovering, except in pathological
    // cases), so a single read on enter is enough.
    let rect: DOMRect | null = null;
    const refresh = () => {
      rect = el.getBoundingClientRect();
    };
    const invalidate = () => {
      rect = null;
    };

    const onEnter = () => {
      refresh();
    };
    const onMove = (e: MouseEvent) => {
      if (!rect) refresh();
      const r = rect!;
      const x = (e.clientX - r.left) / r.width;
      const y = (e.clientY - r.top) / r.height;
      const rotateY = (x - 0.5) * maxTilt * 2;
      const rotateX = -(y - 0.5) * maxTilt * 2;
      el.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg)`;
      el.style.setProperty("--mx", `${x * 100}%`);
      el.style.setProperty("--my", `${y * 100}%`);
    };
    const onLeave = () => {
      el.style.transform = "";
      el.style.removeProperty("--mx");
      el.style.removeProperty("--my");
      rect = null;
    };

    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    // If the user scrolls or the page resizes while hovering, the cached
    // rect is stale — invalidate so the next mousemove refreshes it.
    window.addEventListener("scroll", invalidate, { passive: true });
    window.addEventListener("resize", invalidate, { passive: true });
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
      window.removeEventListener("scroll", invalidate);
      window.removeEventListener("resize", invalidate);
    };
  }, [reduced, maxTilt]);

  return ref;
}
